/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, February 22nd 2022, 11:16:39 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  useAPIFetchPaginatedList,
  APISERVICEPATHS,
  UseLazyFetchListArgs,
  useAPILazyFetchList,
  useAPIForm,
} from "lib/api";
import {
  InstallPreBuiltServiceValidationSchema,
  InstallPreBuiltService,
  InstallPreBuiltServiceResponse,
  ProcessTemplateCategory,
  PreBuiltService,
} from "lib/schemas";

const prebuiltServicesAPIPath = APISERVICEPATHS.prebuiltServices;
const prebuiltServiceCategoriesAPIPath = APISERVICEPATHS.prebuiltServiceCategories;

export const useAPIFetchPreBuiltServiceCategories = () => {
  const { items: prebuilt_service_categories, ...otherProperties } = useAPIFetchPaginatedList<ProcessTemplateCategory>({
    apiServicePath: prebuiltServiceCategoriesAPIPath,
    filterByGlobalOrganization: false,
    makeRequestOnInitialMount: true,
  });

  return { prebuilt_service_categories, ...otherProperties };
};

export const useInstallPreBuiltServiceAPIForm = ({
  prebuiltServiceId,
  defaultServiceCode,
  organizationId,
  installationFile,
}) => {
  const preBuiltInstallationDefault: InstallPreBuiltService = {
    // Not using setState because we do not need to fetch and display the response. So this is just to set some default values
    organization_id: organizationId,
    service_code: defaultServiceCode,
    site_id: null,
    installation_file: installationFile,
  };

  const [installPreBuiltServiceResponse, setInstallPreBuiltServiceResponse] =
    React.useState<InstallPreBuiltServiceResponse>();

  const useAPIFormReturn = useAPIForm<InstallPreBuiltService, InstallPreBuiltServiceResponse>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiServicePath: prebuiltServicesAPIPath,
    apiFormDefaultValues: preBuiltInstallationDefault,
    apiFormDataValidationSchema: InstallPreBuiltServiceValidationSchema,
    setAPIFormResponse: setInstallPreBuiltServiceResponse,
    apiFormActionType: "generic_api_action",
    method: prebuiltServiceId ? "PUT" : "POST",
    apiPathParams: prebuiltServiceId && [prebuiltServiceId],
    formResetDependencyArray: [preBuiltInstallationDefault?.installation_file],
  });

  return { useAPIFormReturn, installPreBuiltServiceResponse };
};

export const useAPILazyLoadPreBuiltServiceTemplates = (
  options: Omit<UseLazyFetchListArgs<PreBuiltService>, "apiServicePath">
) =>
  useAPILazyFetchList<PreBuiltService>({
    apiServicePath: prebuiltServicesAPIPath,
    ...options,
  });
